import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { User, X } from "@phosphor-icons/react";

/**
 * Floating, dismissible Login/Signup prompt for guests.
 * Shows once a guest user has interacted (items in cart) and isn't logged in.
 * Dismissed state persists in sessionStorage so it doesn't keep popping back.
 *
 * Props:
 *   returnTo: route to send the user back to after login (defaults to current path).
 *   alwaysShow: bypass the "items in cart" gating (e.g., on a product page).
 */
export default function GuestLoginPrompt({ returnTo, alwaysShow = false }) {
  const { user, loading } = useAuth();
  const { activeCart } = useCart();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("qd_login_prompt_dismissed") === "1"
  );

  useEffect(() => {
    // Reset dismissed flag once the user logs in so a future logout/guest flow re-shows it
    if (user) sessionStorage.removeItem("qd_login_prompt_dismissed");
  }, [user]);

  if (loading || user || dismissed) return null;
  const hasCart = activeCart && activeCart.count > 0;
  if (!alwaysShow && !hasCart) return null;

  const target = returnTo || location.pathname;

  const dismiss = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    sessionStorage.setItem("qd_login_prompt_dismissed", "1");
    setDismissed(true);
  };

  const onLoginClick = () => {
    sessionStorage.setItem("qd_return_to", target);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[60] max-w-[300px] fade-up"
      data-testid="guest-login-prompt">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-3 pr-2 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#F8CB46]/30 flex items-center justify-center flex-shrink-0">
          <User weight="fill" size={18} className="text-[#15803D]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-black text-sm leading-tight">Login to checkout</div>
          <div className="text-[11px] text-gray-500 leading-tight mt-0.5">
            {hasCart ? `${activeCart.count} item${activeCart.count > 1 ? "s" : ""} in cart` : "Save addresses, track orders"}
          </div>
        </div>
        <Link to="/login" onClick={onLoginClick}
          className="bg-[#15803D] text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-[#166534] active:scale-95 transition shrink-0"
          data-testid="guest-login-prompt-login">
          Login
        </Link>
        <button onClick={dismiss}
          aria-label="Dismiss"
          className="w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 shrink-0"
          data-testid="guest-login-prompt-dismiss">
          <X size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}
